import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from '../hooks/useAuth';
import { useAnalyticsTracking } from '../hooks/useAnalyticsTracking';
import { isInAppBrowser } from '../utils/browserDetection';
import { AuthLayout, GoogleSignInButton, MicrosoftSignInButton, PasswordInput, OrDivider } from '../components/auth';
import { identify, resendVerification, OAuthProviderId } from '../services/emailAuthService';
import { startOAuth as beginOAuth } from '../utils/oauthUtils';

/**
 * Login Page - identifier-first sign-in
 *
 * Stage 1 shows ONLY an email field (plus the OAuth buttons). Submitting the
 * email looks up the account and routes to the right next step:
 * - OAuth account   → prompt to continue with that provider
 * - password account → password field
 * - unverified      → resend-verification prompt
 * - unknown email   → offer to create an account
 */

type LoginStage = 'email' | 'password' | 'oauth' | 'unverified' | 'none';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState<'google' | 'microsoft' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { error: authError, clearError, login } = useAuth();
  const { trackEmailSignup } = useAnalyticsTracking();
  const [detectedInAppBrowser] = useState(() => isInAppBrowser());

  const [stage, setStage] = useState<LoginStage>('email');
  const [email, setEmail] = useState<string>(() => {
    // Prefill when redirected from Signup ("account already exists")
    const params = new URLSearchParams(window.location.search);
    return (location.state as any)?.email || params.get('email') || '';
  });
  const [password, setPassword] = useState('');
  const [providers, setProviders] = useState<OAuthProviderId[]>([]);
  const [hasPassword, setHasPassword] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

  useEffect(() => {
    // Clear any existing auth errors when component mounts
    clearError();

    // Check for error parameters in the URL or state
    const params = new URLSearchParams(location.search);
    const errorParam = params.get('error');
    const messageParam = params.get('message');
    const stateError = (location.state as any)?.error;

    if (errorParam) {
      if (errorParam === 'auth_failed') {
        setError(t('common:errors.authFailed', 'Authentication failed. Please try again.'));
      } else if (errorParam === 'server_error') {
        setError(
          messageParam || t('common:errors.serverError', 'Server error occurred. Please try again later.')
        );
      } else if (errorParam === 'token_generation_failed') {
        setError(t('common:errors.connectionFailed', 'Failed to complete authentication. Please try again.'));
      } else {
        setError(t('common:errors.authFailed', `Authentication error: ${errorParam}`));
      }
    } else if (stateError) {
      setError(stateError);
    }
  }, [location, clearError, t]);

  // Update error state when auth context error changes
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const providerName = (p: OAuthProviderId) => (p === 'google' ? 'Google' : 'Microsoft');

  const resetToEmailStage = () => {
    setStage('email');
    setPassword('');
    setProviders([]);
    setHasPassword(false);
    setResendState('idle');
    setError(null);
  };

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || identifying) return;
    setError(null);
    setIdentifying(true);
    try {
      const result = await identify(trimmed);
      if (result.status === 'oauth') {
        setProviders(result.providers || []);
        setHasPassword(!!result.hasPassword);
        setStage('oauth');
      } else if (result.status === 'password') {
        // Providers may also be linked — offered as a secondary option below
        setProviders(result.providers || []);
        setStage('password');
      } else if (result.status === 'unverified') {
        setStage('unverified');
      } else {
        setStage('none');
      }
    } catch {
      setError(t('common:errors.serverError', 'Server error occurred. Please try again later.'));
    } finally {
      setIdentifying(false);
    }
  };

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || signingIn) return;
    setError(null);
    setSigningIn(true);
    try {
      const result = await login(email.trim(), password);
      if (!result.success && result.code === 'email_not_verified') {
        setStage('unverified');
      }
    } finally {
      setSigningIn(false);
    }
  };

  /**
   * The email is already confirmed as having no account, so skip /signup
   * (which would only re-ask for the same email) and go straight to
   * /signup/continue, which registers it and shows "Check your email".
   */
  const handleCreateAccount = () => {
    trackEmailSignup();
    navigate('/signup/continue', { state: { email: email.trim() } });
  };

  const handleResendVerification = async () => {
    if (resendState !== 'idle') return;
    setResendState('sending');
    try {
      await resendVerification(email.trim());
    } finally {
      setResendState('sent');
    }
  };

  const startOAuth = (provider: 'google' | 'microsoft') => {
    setError(null);
    setIsLoading(true);
    setLoadingProvider(provider);
    try {
      // mode=login shows only the account picker (no re-consent) for returning users
      beginOAuth(provider, 'login');
    } catch (err) {
      setError(t('common:errors.connectionFailed', 'Failed to connect. Please try again.'));
      setIsLoading(false);
      setLoadingProvider(null);
    }
  };

  const changeEmailLink = (
    <button
      type="button"
      onClick={resetToEmailStage}
      className="text-sm font-medium text-primary hover:text-primary-700"
    >
      {t('common:auth.useDifferentEmail', 'Use a different email')}
    </button>
  );

  const emailChip = (
    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
      <span className="text-sm text-gray-900 truncate">{email.trim()}</span>
      {changeEmailLink}
    </div>
  );

  return (
    <AuthLayout
      title={t('common:auth.welcomeBack', 'Welcome back')}
      subtitle={t('common:auth.signInSubtitle', 'Let\'s get your scheduling off to a smooth start')}
      mode="login"
      error={error}
      isInAppBrowser={detectedInAppBrowser}
    >
      {stage === 'email' && (
        <>
          <form onSubmit={handleEmailContinue} className="space-y-4" noValidate>
            <div>
              <label htmlFor="login-email" className="form-label">
                {t('common:auth.emailLabel', 'Email address')}
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-base w-full"
                disabled={identifying || isLoading}
              />
            </div>
            <button type="submit" className="btn-primary btn-lg w-full" disabled={identifying || isLoading || !email.trim()}>
              {identifying
                ? t('common:auth.checkingEmail', 'Checking...')
                : t('common:actions.continue', 'Continue')}
            </button>
          </form>

          <OrDivider />

          <GoogleSignInButton
            onClick={(e) => { e.preventDefault(); startOAuth('google'); }}
            isLoading={loadingProvider === 'google'}
            disabled={isLoading || identifying}
            mode="signin"
          />
          <MicrosoftSignInButton
            onClick={(e) => { e.preventDefault(); startOAuth('microsoft'); }}
            isLoading={loadingProvider === 'microsoft'}
            disabled={isLoading || identifying}
            mode="signin"
          />
        </>
      )}

      {stage === 'password' && (
        <>
        <form onSubmit={handlePasswordSignIn} className="space-y-4" noValidate>
          {emailChip}
          <PasswordInput
            id="login-password"
            value={password}
            onChange={setPassword}
            label={t('common:auth.passwordLabel', 'Password')}
            autoComplete="current-password"
            disabled={signingIn}
            labelAction={
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary hover:text-primary-700"
              >
                {t('common:auth.forgotPassword', 'Forgot your password?')}
              </Link>
            }
          />
          <button type="submit" className="btn-primary btn-lg w-full" disabled={signingIn || !password}>
            {signingIn
              ? t('common:auth.signingIn', 'Signing in...')
              : t('common:auth.signIn', 'Sign In')}
          </button>
        </form>

        {providers.length > 0 && (
          <>
            <OrDivider />
            {providers.includes('google') && (
              <GoogleSignInButton
                onClick={(e) => { e.preventDefault(); startOAuth('google'); }}
                isLoading={loadingProvider === 'google'}
                disabled={isLoading || signingIn}
                mode="signin"
              />
            )}
            {providers.includes('microsoft') && (
              <MicrosoftSignInButton
                onClick={(e) => { e.preventDefault(); startOAuth('microsoft'); }}
                isLoading={loadingProvider === 'microsoft'}
                disabled={isLoading || signingIn}
                mode="signin"
              />
            )}
          </>
        )}
        </>
      )}

      {stage === 'oauth' && (
        <div className="space-y-4">
          {emailChip}
          <div className="bg-primary-50 border-l-4 border-primary p-4 rounded-r">
            <p className="text-sm text-primary-700">
              {t('common:auth.accountUsesProvider', 'This account signs in with {{provider}}. Continue below.', {
                provider: providers.map(providerName).join(' / '),
              })}
            </p>
          </div>
          {providers.includes('google') && (
            <GoogleSignInButton
              onClick={(e) => { e.preventDefault(); startOAuth('google'); }}
              isLoading={loadingProvider === 'google'}
              disabled={isLoading}
              mode="signin"
            />
          )}
          {providers.includes('microsoft') && (
            <MicrosoftSignInButton
              onClick={(e) => { e.preventDefault(); startOAuth('microsoft'); }}
              isLoading={loadingProvider === 'microsoft'}
              disabled={isLoading}
              mode="signin"
            />
          )}
          {hasPassword && (
            <button
              type="button"
              onClick={() => setStage('password')}
              className="w-full text-sm font-medium text-primary hover:text-primary-700"
            >
              {t('common:auth.signInWithPasswordInstead', 'Sign in with your password instead')}
            </button>
          )}
        </div>
      )}

      {stage === 'unverified' && (
        <div className="space-y-4">
          {emailChip}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r">
            <p className="text-sm text-yellow-800">
              {t('common:auth.emailNotVerified', 'This email address hasn\'t been verified yet. Check your inbox for the verification link to finish creating your account.')}
            </p>
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={resendState !== 'idle'}
              className="mt-2 text-sm font-medium text-primary hover:text-primary-700 disabled:text-gray-400"
            >
              {resendState === 'sent'
                ? t('common:auth.verificationEmailSent', 'Verification email sent!')
                : resendState === 'sending'
                ? t('common:auth.sendingVerification', 'Sending...')
                : t('common:auth.resendVerification', 'Resend verification email')}
            </button>
          </div>
        </div>
      )}

      {stage === 'none' && (
        <div className="space-y-4">
          {emailChip}
          <div className="bg-gray-50 border-l-4 border-gray-300 p-4 rounded-r">
            <p className="text-sm text-gray-700">
              {t('common:auth.noAccountFound', 'We couldn\'t find an account for this email address.')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCreateAccount}
            className="btn-primary btn-lg w-full"
          >
            {t('common:auth.createAccountInstead', 'Create an account')}
          </button>
        </div>
      )}
    </AuthLayout>
  );
};

export default Login;
